import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const INK_COLOR = '#1a1a1a';
const PAD_BACKGROUND = '#ffffff';
const STROKE_WIDTH = 2.5;
const MAX_DPR = 3;
// Keep exported PNGs inside the server-side 2048x1024 bound.
const MAX_EXPORT_WIDTH = 2048;
const MAX_EXPORT_HEIGHT = 1024;

/**
 * Web signature pad — real <canvas> with Pointer Events + pointer capture.
 *
 * Shared adapter contract (mirrors SignaturePad.native.jsx):
 * - props: `strokes` (controlled, array of point arrays in CSS px relative to
 *   the pad), `onStrokeChange(nextStrokes, { isEmpty, finished })`, `disabled`,
 *   `height`, `style`, `testID`
 * - ref: `exportPng()` -> Promise<{ base64, mimeType, width, height }>,
 *   `clear()`
 *
 * The pad is intentionally white with dark ink in every theme — it produces
 * exported evidence, not themed UI chrome.
 */
const SignaturePad = forwardRef(function SignaturePad(
    { strokes = [], onStrokeChange, disabled = false, height = 200, style = null, testID },
    ref,
) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const activePointerIdRef = useRef(null);
    const strokesRef = useRef(strokes);
    // Mirror the controlled prop — but never mid-gesture: a parent re-render
    // with a stale `strokes` would clobber points already captured in the ref.
    if (!drawingRef.current) strokesRef.current = strokes;

    const redraw = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.fillStyle = PAD_BACKGROUND;
        ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
        ctx.strokeStyle = INK_COLOR;
        ctx.fillStyle = INK_COLOR;
        ctx.lineWidth = STROKE_WIDTH;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (const stroke of strokesRef.current) {
            if (!stroke.length) continue;
            if (stroke.length === 1) {
                // Tap dot — draw a small filled circle.
                ctx.beginPath();
                ctx.arc(stroke[0].x, stroke[0].y, STROKE_WIDTH / 2 + 0.5, 0, Math.PI * 2);
                ctx.fill();
                continue;
            }
            ctx.beginPath();
            ctx.moveTo(stroke[0].x, stroke[0].y);
            for (let i = 1; i < stroke.length; i++) {
                const prev = stroke[i - 1];
                const point = stroke[i];
                // Midpoint quadratic smoothing — same shape on resize since
                // points are stored in CSS px.
                ctx.quadraticCurveTo(prev.x, prev.y, (prev.x + point.x) / 2, (prev.y + point.y) / 2);
            }
            ctx.lineTo(stroke[stroke.length - 1].x, stroke[stroke.length - 1].y);
            ctx.stroke();
        }
    };

    // Size the backing store to layout size * DPR and keep strokes on resize.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
            const w = Math.round(rect.width * dpr);
            const h = Math.round(rect.height * dpr);
            if (canvas.width !== w || canvas.height !== h) {
                canvas.width = w;
                canvas.height = h;
            }
            redraw();
        };
        resize();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
        observer?.observe(canvas);
        window.addEventListener('resize', resize);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', resize);
        };
    }, []);

    // Repaint whenever the controlled strokes change.
    useEffect(() => {
        redraw();
    }, [strokes]);

    const pointFromEvent = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        // Zero-size rect (before first layout) would clamp every point to the
        // origin — keep raw offsets until real bounds exist.
        if (!rect.width || !rect.height) {
            return { x: e.clientX - rect.left, y: e.clientY - rect.top };
        }
        return {
            x: Math.min(Math.max(e.clientX - rect.left, 0), rect.width),
            y: Math.min(Math.max(e.clientY - rect.top, 0), rect.height),
        };
    };

    const isActivePointer = (e) => {
        // Events without a pointerId (window blur, synthetic calls) always
        // apply; real pointer events must belong to the drawing pointer.
        if (e.pointerId == null || activePointerIdRef.current == null) return true;
        return e.pointerId === activePointerIdRef.current;
    };

    const finishStroke = (e) => {
        if (!drawingRef.current || !isActivePointer(e)) return;
        drawingRef.current = false;
        const pointerId = activePointerIdRef.current;
        activePointerIdRef.current = null;
        try {
            if (pointerId != null) canvasRef.current?.releasePointerCapture?.(pointerId);
        } catch { /* noop */ }
        // Repaint synchronously — a finished event can be followed by an
        // export before React has flushed the trailing prop change.
        redraw();
        onStrokeChange?.(strokesRef.current, { isEmpty: strokesRef.current.length === 0, finished: true });
    };

    const handlePointerDown = (e) => {
        if (disabled || (e.button != null && e.button > 0)) return;
        if (drawingRef.current) {
            if (e.pointerId !== activePointerIdRef.current) return; // second finger — ignore
            // Missed pointerup (released outside the window, capture revoked,
            // element unmounted mid-gesture) — close the stale stroke before
            // starting a new one so the click can't merge into old ink.
            finishStroke(e);
        }
        e.preventDefault();
        canvasRef.current?.setPointerCapture?.(e.pointerId);
        drawingRef.current = true;
        activePointerIdRef.current = e.pointerId;
        const next = [...strokesRef.current, [pointFromEvent(e)]];
        strokesRef.current = next;
        // Paint immediately — the canvas is the export surface, so it must
        // never lag behind strokesRef (the parent's state flushes a frame
        // later, and exportPng can fire on the very next finished event).
        redraw();
        onStrokeChange?.(next, { isEmpty: false, finished: false });
    };

    const handlePointerMove = (e) => {
        if (!drawingRef.current || disabled || !isActivePointer(e)) return;
        e.preventDefault();
        const point = pointFromEvent(e);
        const next = strokesRef.current.slice();
        next[next.length - 1] = [...next[next.length - 1], point];
        strokesRef.current = next;
        redraw();
        onStrokeChange?.(next, { isEmpty: false, finished: false });
    };

    // Element-level pointerup can be missed: release outside the window,
    // capture revoked by the browser, or the canvas swapped mid-gesture.
    // Window listeners + lostpointercapture + blur close the stroke no
    // matter where the pointer went.
    const finishStrokeRef = useRef(finishStroke);
    finishStrokeRef.current = finishStroke;
    useEffect(() => {
        const onUp = (e) => finishStrokeRef.current?.(e);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        window.addEventListener('blur', onUp);
        return () => {
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            window.removeEventListener('blur', onUp);
        };
    }, []);

    useImperativeHandle(ref, () => ({
        clear: () => {
            // Also end any gesture in progress — otherwise a clear during a
            // drag leaves drawingRef stuck and the next move re-inks.
            drawingRef.current = false;
            activePointerIdRef.current = null;
            strokesRef.current = [];
            redraw();
            onStrokeChange?.([], { isEmpty: true, finished: true });
        },
        exportPng: async () => {
            const canvas = canvasRef.current;
            if (!canvas) throw new Error('Signature pad unavailable.');
            // Bounded export: pad is capped by form width (~640px) * DPR<=3 and
            // height prop; additionally clamp canvas pixels defensively.
            const scale = Math.min(1, MAX_EXPORT_WIDTH / canvas.width, MAX_EXPORT_HEIGHT / canvas.height);
            let source = canvas;
            if (scale < 1) {
                const shrunk = document.createElement('canvas');
                shrunk.width = Math.round(canvas.width * scale);
                shrunk.height = Math.round(canvas.height * scale);
                shrunk.getContext('2d').drawImage(canvas, 0, 0, shrunk.width, shrunk.height);
                source = shrunk;
            }
            const dataUrl = source.toDataURL('image/png');
            const commaIdx = dataUrl.indexOf(',');
            return {
                base64: commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl,
                mimeType: 'image/png',
                width: source.width,
                height: source.height,
            };
        },
    }), [onStrokeChange]);

    return (
        <canvas
            ref={canvasRef}
            data-testid={testID}
            style={{
                display: 'block',
                width: '100%',
                height,
                backgroundColor: PAD_BACKGROUND,
                borderRadius: 10,
                touchAction: 'none',
                cursor: disabled ? 'not-allowed' : 'crosshair',
                opacity: disabled ? 0.6 : 1,
                ...(style || {}),
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishStroke}
            onPointerCancel={finishStroke}
            onLostPointerCapture={finishStroke}
            aria-label="Signature pad. Draw your signature with a mouse, touch, or stylus."
        />
    );
});

export default SignaturePad;
